import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// D1/SQLite's own datetime('now') (used all over private/auth's
// decided_at/created_at/etc. writes) produces "YYYY-MM-DD HH:MM:SS" —
// space-separated, no 'T', no timezone marker — always UTC in practice,
// but a bare `new Date(...)` in a browser parses a string with THIS
// exact shape as LOCAL time, not UTC (only a proper ISO 8601 string with
// a 'T' separator AND an explicit offset/Z is parsed as UTC/that
// offset). Confirmed live, 2026-09-10: a freshly-approved
// approved_box_components row showed "-21599s ago" (off by ~6h, this
// browser's own UTC offset) — the exact same bug was already present on
// every OTHER decided_at in this app (approveBox, the pre-existing
// approveComponent path), just invisible: a several-hour parse error
// silently absorbs into "22d ago" at day granularity and only becomes
// obviously wrong for a JUST-written timestamp. Normalizing here, once,
// fixes every caller (relativeTime below, and this function) for every
// past and future write using this exact convention — cheaper and less
// risky than touching every `datetime('now')` call site in private/auth.
function parseServerTimestamp(ts: string | number): Date {
  if (
    typeof ts === "string" &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ts)
  ) {
    return new Date(ts.replace(" ", "T") + "Z");
  }
  return new Date(ts);
}

export function formatDate(ts: string | number | null | undefined): string {
  if (!ts) return "—";
  const d = parseServerTimestamp(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function relativeTime(ts: string | number | null | undefined): string {
  if (!ts) return "—";
  const t = parseServerTimestamp(ts).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(ts);
}
